import { CaseStudySection } from '@/types/case-study';

/**
 * Checks if a case study section has meaningful content
 * A section is considered empty if:
 * - No rich text content (or only whitespace)
 * - No images
 * - No content blocks
 */
export function hasSectionContent(section: CaseStudySection): boolean {
  // Check if there's meaningful rich text content
  const hasRichTextContent = section.content && 
    section.content.trim().length > 0 && 
    section.content !== '<p></p>' && 
    section.content !== '<p><br></p>';

  // Check if there are images
  const hasImages = section.images && section.images.length > 0;

  // Check if there are content blocks with content
  const hasContentBlocks = section.content_blocks && 
    section.content_blocks.length > 0 &&
    section.content_blocks.some(block => {
      // Check if the content block has meaningful content
      if (block.type === 'text') {
        return block.content && 
          typeof block.content === 'string' && 
          block.content.trim().length > 0 &&
          block.content !== '<p></p>' &&
          block.content !== '<p><br></p>';
      }
      if (block.type === 'image' || block.type === 'video') {
        return block.content && block.content.url;
      }
      if (block.type === 'gallery') {
        return block.content && 
          Array.isArray(block.content.images) && 
          block.content.images.length > 0;
      }
      if (block.type === 'quote') {
        return block.content && 
          block.content.text && 
          block.content.text.trim().length > 0;
      }
      // Line breaks and other simple blocks are considered content
      return true;
    });

  return hasRichTextContent || hasImages || hasContentBlocks;
}

/**
 * Filters out empty sections from a case study
 * Only returns sections that have meaningful content
 */
export function filterEmptySections(sections: CaseStudySection[]): CaseStudySection[] {
  return sections.filter(hasSectionContent);
}

/**
 * Gets the list of common section types that should be checked for content
 * These are the sections mentioned in the user's requirements
 */
export const COMMON_SECTION_TYPES = [
  'problem-statement',
  'goals-objectives', 
  'design-process',
  'key-decisions',
  'outcome-impact',
  'learnings-reflections',
  'conclusion'
] as const;

/**
 * Checks if a section type is one of the common sections that should be filtered
 */
export function isCommonSectionType(sectionType: string): boolean {
  return COMMON_SECTION_TYPES.includes(sectionType as any);
}
